/**
 * Dados do painel do ambiente, lidos da linha da planilha: pessoas, datas,
 * terceiros (com quantidade e descritivo de cada categoria) e acabamentos.
 * Funções puras.
 */
import { formatarData } from "../../utils/calculos";
import { hasValue, toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { toDate } from "../../utils/status";

const SEM_VALOR = "—";

/** Categoria de terceiro: coluna de status (X/data = recebido), quantidade e descritivo. */
export const TERCEIROS = Object.freeze([
  { id: "serralheria", rotulo: "Serralheria", status: F.SERRALHERIA_TERCEIROS, qtd: F.SERRALHERIA_QTD, descritivo: F.SERRALHERIA_DESCRITIVO },
  { id: "perfil", rotulo: "Perfil alumínio", status: F.PERFIL_ALUMINIO_TERCEIROS, qtd: F.PERFIL_ALUMINIO_QTD, descritivo: F.PERFIL_ALUMINIO_DESCRITIVO },
  { id: "portas", rotulo: "Portas/Frentes", status: F.PORTAS_FRENTES_TERCEIROS, qtd: F.PORTAS_FRENTES_QTD, descritivo: F.PORTAS_FRENTES_DESCRITIVO },
  { id: "vidracaria", rotulo: "Vidraçaria", status: F.VIDRACARIA, qtd: F.VIDRACARIA_QTD, descritivo: F.VIDRACARIA_DESCRITIVO },
  { id: "tapecaria", rotulo: "Tapeçaria", status: F.TAPECARIA_TERCEIROS, qtd: F.TAPECARIA_QTD, descritivo: F.TAPECARIA_DESCRITIVO },
  { id: "ferragens", rotulo: "Ferragens", status: F.FERRAGENS_ESPECIAIS_TERCEIROS, qtd: F.FERRAGENS_ESPECIAIS_QTD,
    descritivo: F.FERRAGENS_ESPECIAIS_DESCRITIVO, naoSeAplica: F.FERRAGENS_ESPECIAIS_CELULA },
]);

/** Acabamentos: coluna de status por tipo; o descritivo da planilha é um só (AK). */
export const ACABAMENTOS = Object.freeze([
  { id: "pintura", rotulo: "Pintura", status: F.PINTURA },
  { id: "verniz", rotulo: "Verniz", status: F.VERNIZ_ACAB, qtd: F.VERNIZ_QTD },
  { id: "pintura-perfil", rotulo: "Pintura perfil", status: F.PINTURA_PERFIL },
  { id: "pintura-serralheria", rotulo: "Pintura serralheria", status: F.PINTURA_SERRALHERIA },
]);

const PARECE_DATA = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/;
/** "0" na coluna de status ou de quantidade = o ambiente não tem esse item. */
const ZERO = /^0+([.,]0+)?$/;

function texto(valor) {
  return hasValue(valor) ? String(valor).trim() : "";
}

function temQuantidade(valor) {
  const t = texto(valor);
  return Boolean(t) && t.toUpperCase() !== "X" && !ZERO.test(t);
}

/** "X" ou uma data na coluna de status = concluído; outro texto = situação em aberto. */
export function situacao(valor, rotuloOk) {
  const t = texto(valor);
  if (!t || ZERO.test(t)) return null;
  if (t.toUpperCase() === "X" || PARECE_DATA.test(t) || valor instanceof Date) return { ok: true, rotulo: rotuloOk };
  return { ok: false, rotulo: t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() };
}

/** Descritivo da planilha em itens: uma linha (ou trecho separado por ";") por item. */
export function itensDoDescritivo(valor) {
  return texto(valor).split(/\r?\n|;/).map((l) => l.trim()).filter(Boolean);
}

export function terceirosDoAmbiente(row) {
  const categorias = TERCEIROS
    .filter((c) => !(c.naoSeAplica && texto(row?.[c.naoSeAplica]).toUpperCase() === "X"))
    .map((c) => {
      const marcada = situacao(row?.[c.status], "OK");
      const itens = itensDoDescritivo(row?.[c.descritivo]);
      const qtd = temQuantidade(row?.[c.qtd]) ? texto(row[c.qtd]) : "";
      if (!marcada && !itens.length && !qtd) return null;
      const estado = marcada || { ok: false, rotulo: "Aguardando" };
      return { id: c.id, rotulo: c.rotulo, ok: estado.ok, situacao: estado.rotulo, qtd, itens: itens.length ? itens : [c.rotulo] };
    })
    .filter(Boolean);
  const ok = categorias.filter((c) => c.ok).length;
  const itensPendentes = categorias.filter((c) => !c.ok).reduce((s, c) => s + c.itens.length, 0);
  return { categorias, ok, total: categorias.length, itensPendentes };
}

export function acabamentosDoAmbiente(row) {
  const descritivo = texto(row?.[F.ACABAMENTO_DESCRITIVO]);
  const itens = ACABAMENTOS
    .map((a) => {
      const estado = situacao(row?.[a.status], "Concluído");
      const qtd = a.qtd && temQuantidade(row?.[a.qtd]) ? texto(row[a.qtd]) : "";
      if (!estado && !qtd) return null;
      return { id: a.id, rotulo: a.rotulo, ok: estado ? estado.ok : false, situacao: estado ? estado.rotulo : "Aguardando", qtd };
    })
    .filter(Boolean);
  return { itens, descritivo, ok: itens.filter((i) => i.ok).length, total: itens.length };
}

function pessoa(valor, papel) {
  const nome = toDisplayText(valor, "");
  return { nome: nome && nome !== SEM_VALOR ? nome : "A definir", papel, definido: Boolean(nome && nome !== SEM_VALOR) };
}

export function pessoasDoAmbiente(row) {
  return [pessoa(row?.[F.VENDEDOR], "vendedor"), pessoa(row?.[F.CONSULTOR], "consultor"), pessoa(row?.[F.MARCENEIRO_RESPONSAVEL], "marceneiro")];
}

export function datasDoAmbiente(row) {
  const prevista = toDate(row?.[F.ENTREGA_PREVISTA]);
  const reprogramada = toDate(row?.[F.DATA_REPROGRAMADA]);
  const prazo = texto(row?.[F.PRAZO_EM_DIAS]);
  const mudou = Boolean(prevista && reprogramada && prevista.toDateString() !== reprogramada.toDateString());
  return [
    { rotulo: "Venda", valor: formatarData(row?.[F.DATA_DA_VENDA]) },
    { rotulo: "Entrega prevista", valor: formatarData(prevista) },
    { rotulo: "Reprogramada", valor: formatarData(reprogramada), alerta: mudou },
    { rotulo: "Prazo", valor: prazo ? `${prazo} dias` : SEM_VALOR },
  ];
}
